// src/context/AuthContext.jsx
import React, { createContext, useState, useEffect, useContext } from 'react';
import { authService } from '../services/auth';

// Create the context
const AuthContext = createContext();

// Create a custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initialize auth state from localStorage or server
    const init = async () => {
      const localUser = authService.getCurrentUser();
      if (localUser) {
        setUser(localUser);
        setIsAuthenticated(true);
      }

      // Try to refresh user from server to get fresh data/cookie auth
      try {
        const res = await authService.me();
        if (res.user) {
          setUser(res.user);
          setIsAuthenticated(true);
        }
      } catch (err) {
        // ignore errors (user not logged in)
      }

      setLoading(false);
    };

    init();
  }, []);

  const login = async (email, password) => {
    try {
      const res = await authService.login({ email, password });
      if (res.token) {
        setUser(res.user || authService.getCurrentUser());
        setIsAuthenticated(true);
        return { success: true };
      }
      return { success: false, error: res.message || res.error || 'Unable to sign in. Please check your credentials.' };
    } catch (err) {
      return { success: false, error: err.userMessage || err.response?.data?.message || 'Unable to sign in. Please try again.' };
    }
  };

  const signup = async (userData) => {
    try {
      const res = await authService.register(userData);
      // Check if registration successful but requires email verification
      if (res.status === 'success' && res.data?.emailVerificationRequired) {
        return { 
          success: true, 
          requiresVerification: true,
          email: res.data.email,
          message: res.message || 'Please check your email to verify your account.'
        };
      }
      // Old flow for backward compatibility (if token is returned)
      if (res.token) {
        setUser(res.user || authService.getCurrentUser());
        setIsAuthenticated(true);
        return { success: true };
      }
      return { success: false, error: res.message || res.error || 'Unable to create account. Please try again.' };
    } catch (err) {
      return { success: false, error: err.userMessage || err.response?.data?.message || 'Unable to create account. Please try again.' };
    }
  };

  const logout = async () => {
    await authService.logout();
    setIsAuthenticated(false);
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const res = await authService.me();
      if (res.user) {
        setUser(res.user);
        setIsAuthenticated(true);
        return res.user;
      }
    } catch (err) {
      setIsAuthenticated(false);
      setUser(null);
    }
    return null;
  };

  const updatePassword = async (currentPassword, newPassword) => {
    try {
      const res = await authService.updatePassword(currentPassword, newPassword);
      if (res.status === 'success') {
        return { success: true, message: res.message || 'Password updated' };
      }
      return { success: false, error: res.message || 'Failed to update password' };
    } catch (err) {
      return { success: false, error: err.response?.data?.message || err.message };
    }
  };

  const googleAuth = async (tokenId) => {
    try {
      const res = await authService.googleAuth(tokenId);
      if (res.token) {
        setUser(res.user || authService.getCurrentUser());
        setIsAuthenticated(true);
        return { success: true };
      }
      return { success: false, error: res.message || res.error || 'Google auth failed' };
    } catch (err) {
      return { success: false, error: err.response?.data?.message || err.message };
    }
  };

  const appleAuth = async (identityToken, user) => {
    try {
      const res = await authService.appleAuth({ identityToken, user });
      if (res.token) {
        setUser(res.user || authService.getCurrentUser());
        setIsAuthenticated(true);
        return { success: true };
      }
      return { success: false, error: res.message || res.error || 'Apple auth failed' };
    } catch (err) {
      return { success: false, error: err.response?.data?.message || err.message };
    }
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    signup,
    logout,
    refreshUser,
    updatePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Export the context itself
export { AuthContext };
